import { apiClient } from './apiClient.js';
import type {
  ApiResponse,
  AuthUser,
  InvitationResponse,
  PaginatedResponse,
  InvitationRequest,
  UserRole,
  WorkspaceMember,
  StageNotificationConfig,
  CreateStageNotificationConfigRequest,
  UpdateStageNotificationConfigRequest,
  MemberAuditLogListResponse,
  CandidateStage,
  StageNotificationType,
} from '@onboarding-dashboard/shared';

export interface TestStageNotificationResult {
  templatePath?: string;
  templateExists?: boolean;
  notificationTypeFound?: boolean;
  knownNotificationTypes?: string[];
  contentEntry?: unknown;
  sendResult?: unknown;
  errorMessage?: string;
  errorStack?: string;
}

export const adminService = {
  async listUsers(
    page: number = 1,
    pageSize: number = 20
  ): Promise<ApiResponse<PaginatedResponse<AuthUser>>> {
    return apiClient.get<PaginatedResponse<AuthUser>>(
      `/admin/users?page=${page}&pageSize=${pageSize}`
    );
  },

  async updateUserRole(userId: string, role: UserRole): Promise<ApiResponse<AuthUser>> {
    return apiClient.patch<AuthUser>(`/admin/users/${userId}/role`, { role });
  },

  async updateUserRoles(userId: string, roles: string[]): Promise<ApiResponse<AuthUser>> {
    return apiClient.patch<AuthUser>(`/admin/users/${userId}/roles`, { roles });
  },

  async listInvitations(
    page: number = 1,
    pageSize: number = 20
  ): Promise<ApiResponse<PaginatedResponse<InvitationResponse>>> {
    return apiClient.get<PaginatedResponse<InvitationResponse>>(
      `/admin/invitations?page=${page}&pageSize=${pageSize}`
    );
  },

  async createInvitation(data: InvitationRequest): Promise<ApiResponse<InvitationResponse>> {
    return apiClient.post<InvitationResponse>('/admin/invitations', data);
  },

  async revokeInvitation(id: string): Promise<ApiResponse<InvitationResponse>> {
    return apiClient.delete<InvitationResponse>(`/admin/invitations/${id}`);
  },

  async listMembers(): Promise<ApiResponse<WorkspaceMember[]>> {
    return apiClient.get<WorkspaceMember[]>('/admin/members');
  },

  async removeUserAccess(
    userId: string
  ): Promise<ApiResponse<{ id: string; email: string; status: string }>> {
    return apiClient.delete<{ id: string; email: string; status: string }>(
      `/admin/users/${userId}`
    );
  },

  async listStageNotificationConfigs(
    openRoleIds: string[]
  ): Promise<ApiResponse<StageNotificationConfig[]>> {
    const ids = openRoleIds.map(encodeURIComponent).join(',');
    return apiClient.get<StageNotificationConfig[]>(
      `/admin/stage-notification-configs?openRoleIds=${ids}`
    );
  },

  async createStageNotificationConfig(
    data: CreateStageNotificationConfigRequest
  ): Promise<ApiResponse<StageNotificationConfig>> {
    return apiClient.post<StageNotificationConfig>('/admin/stage-notification-configs', data);
  },

  async updateStageNotificationConfig(
    id: string,
    enabled: boolean
  ): Promise<ApiResponse<StageNotificationConfig>> {
    const body: UpdateStageNotificationConfigRequest = { enabled };
    return apiClient.patch<StageNotificationConfig>(
      `/admin/stage-notification-configs/${id}`,
      body
    );
  },

  async deleteStageNotificationConfig(id: string): Promise<ApiResponse<{ id: string }>> {
    return apiClient.delete<{ id: string }>(`/admin/stage-notification-configs/${id}`);
  },

  async listAuditLogs(
    page: number = 1,
    pageSize: number = 50
  ): Promise<ApiResponse<MemberAuditLogListResponse>> {
    return apiClient.get<MemberAuditLogListResponse>(
      `/admin/audit-logs?page=${page}&pageSize=${pageSize}`
    );
  },

  async copyStageNotificationConfigs(
    sourceRoleId: string,
    targetRoleId: string
  ): Promise<ApiResponse<{ copied: number; configs: StageNotificationConfig[] }>> {
    return apiClient.post<{ copied: number; configs: StageNotificationConfig[] }>(
      '/admin/stage-notification-configs/copy',
      { sourceRoleId, targetRoleId }
    );
  },

  async testStageNotification(
    notificationType: StageNotificationType,
    stage: CandidateStage
  ): Promise<ApiResponse<TestStageNotificationResult>> {
    return apiClient.post<TestStageNotificationResult>('/admin/test-stage-notification', {
      notificationType,
      stage,
    });
  },
};
